/**
 * PartTwoSlides.tsx
 * -----------------
 * Learning slides for crash course part two.
 * - Multilingual content and video.
 * - Updates progress and points on completion.
 */


import React from 'react';
import { useProgress } from '../../../context/ProgressContext';
import Button from '../../common/Button';
import { useHistory } from 'react-router-dom';
import Box from '../../common/Box';

const t = {
  slide1_title: {
    fi: 'Saunan turvallisuusvinkit',
    en: 'Sauna Safety Tips',
    sv: 'Säkerhetstips för bastu',
  },
  slide1_content: {
    fi: 'Saunan käyttö vaatii huolellisuutta. Tässä osiossa opit tärkeimmät turvallisuusohjeet.',
    en: 'Using a sauna requires care. In this section, you will learn the most important safety instructions.',
    sv: 'Att använda bastu kräver noggrannhet. I det här avsnittet lär du dig de viktigaste säkerhetsanvisningarna.',
  },
  slide2_title: {
    fi: 'Saunan turvallinen käyttö',
    en: 'Safe Use of Sauna',
    sv: 'Säker användning av bastu',
  },
  slide2_content: {
    fi: 'Älä jätä saunaa päälle ilman valvontaa. Tarkista, että kiuas ja laudekalusteet ovat kunnossa.',
    en: 'Never leave the sauna on unattended. Make sure the stove and benches are in good condition.',
    sv: 'Lämna aldrig bastun på utan tillsyn. Kontrollera att aggregatet och lavarna är i gott skick.',
  },
  slide3_title: {
    fi: 'Muista nesteytys',
    en: 'Hydrate',
    sv: 'Kom ihåg att dricka',
  },
  slide3_content: {
    fi: 'Saunassa hikoilet runsaasti, joten muista juoda vettä ennen, aikana ja jälkeen saunomisen. Suositus on vähintään kuusi lasia vettä koko saunakokemuksen aikana.',
    en: 'You’ll sweat about a pint of fluid in any sauna, so it’s important to drink at least six cups of water throughout your experience. Start sipping before you enter, continue while inside, and keep drinking water after you leave.',
    sv: 'Du svettas mycket i bastun, så det är viktigt att dricka vatten före, under och efter bastubadet. Rekommendationen är minst sex glas vatten under hela bastuupplevelsen.',
  },
  slide4_title: {
    fi: 'Rajoita aikaa',
    en: 'Limit Time',
    sv: 'Begränsa tiden',
  },
  slide4_content: {
    fi: 'Perinteisessä saunassa voi olla turvallisesti 15–20 minuuttia kerrallaan. Aloita maltillisesti ja kuuntele kehoasi – jos tunnet olosi liian kuumaksi, poistu saunasta.',
    en: 'You can safely sit in a traditional sauna for 15–20 minutes. Always start with a short session and listen to your body. If you feel too hot, leave the sauna.',
    sv: 'Du kan sitta säkert i en traditionell bastu i 15–20 minuter. Börja alltid försiktigt och lyssna på din kropp – om du känner dig för varm, lämna bastun.',
  },
  slide5_title: {
    fi: 'Vältä alkoholia ja päihteitä',
    en: 'Avoid Alcohol and Recreational Drugs',
    sv: 'Undvik alkohol och droger',
  },
  slide5_content: {
    fi: 'Alkoholi ja päihteet lisäävät kehon kuormitusta saunassa ja voivat aiheuttaa vaaratilanteita. Sauno aina selvin päin ja varmista riittävä nesteytys.',
    en: 'Heating up and sweating intensify the effects of substances on your system, with serious (sometimes fatal) health consequences. Always use the sauna sober and make sure you are well hydrated.',
    sv: 'Alkohol och droger ökar kroppens belastning i bastun och kan orsaka farliga situationer. Bada bastu alltid nykter och se till att du är tillräckligt hydrerad.',
  },
  video_title: {
    fi: 'Katso: Turvallisuusvinkkejä käytännössä',
    en: 'Watch: Practical Safety Tips',
    sv: 'Titta: Praktiska säkerhetstips',
  },
  back: {
    fi: 'Takaisin kurssi yleisnäkymään',
    en: 'Back to course overview',
    sv: 'Tillbaka till kursöversikten',
  },
};

const YOUTUBE_ID = 'l3w92JPoy2A';

export const PartTwoSlides = () => {
  const progress = useProgress();
  const lang = (['en', 'fi', 'sv'].includes(progress.language) ? progress.language : 'fi') as
    | 'en'
    | 'fi'
    | 'sv';
  const history = useHistory();

  const currentPoints = Number(localStorage.getItem('points'));
  const latestCompletedStep = Number(localStorage.getItem('latestCompletedStep'));
  const partTwoBadge = localStorage.getItem('partTwoDone');

  return (
    <div className="flex flex-col min-h-screen justify-evenly p-16">
      <Box className="my-4">
        <h2 className="text-xl font-bold mb-2">{t.slide1_title[lang]}</h2>
        <p>{t.slide1_content[lang]}</p>
      </Box>
      <Box className="my-4">
        <h2 className="text-xl font-bold mb-2">{t.slide2_title[lang]}</h2>
        <p>{t.slide2_content[lang]}</p>
      </Box>
      <Box className="my-4">
        <h2 className="text-xl font-bold mb-2">{t.slide3_title[lang]}</h2>
        <p>{t.slide3_content[lang]}</p>
      </Box>
      <Box className="my-4" itemsCenter={false}>
        <h2 className="text-xl font-bold mb-2">{t.video_title[lang]}</h2>
        <div className="aspect-w-16 aspect-h-9">
          <iframe
            src={`https://www.youtube.com/embed/${YOUTUBE_ID}`}
            title="YouTube video player"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full rounded-lg  "
            style={{ minHeight: '500px' }}
          ></iframe>
        </div>
      </Box>
      <Button
        gradient={false}
        className="bg-gradient-to-r from-green-600 to-green-500 \
        hover:from-green-700 hover:to-green-600 w-1/4 flex-col self-end"
        onClick={() => {
          if (currentPoints != 40 && !partTwoBadge) {
            localStorage.setItem('points', String(currentPoints + 10));
            localStorage.setItem('partTwoDone', 'true');
          }
          if (latestCompletedStep < 2) {
            localStorage.setItem('latestCompletedStep', '2');
          }

          history.push('/crash-course');
        }}
      >
        {t.back[lang]}
      </Button>
    </div>
  );
};
